import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EffectRef,
  inject,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { HeaderSophiaComponent } from "../header-sophia/header-sophia.component";
import { MatIcon } from "@angular/material/icon";
import SearchBarComponent from "../../../../shared/components/search-bar/search-bar.component";
import { TablaPacientesComponent } from "./components/tabla-pacientes/tabla-pacientes.component";
import { PagerComponent } from "../../../../shared/components/pager/pager.component";
import { IListRequestOptions, PacienteService, StorageService, UtilServiceService } from "@shared/services/index";
import { StorageKeys, StorageType } from "../../../../shared/enums";
import { PatientFilterItem, PatientListItem, PatientsResponse } from "@shared/interfaces/paciente.interface";
import { firstValueFrom, map, Observable } from "rxjs";
import { Router } from "@angular/router";
import { CentroMedico, Medicos } from '@shared/interfaces/perfil.interface';

@Component({
  selector: 'soph-mis-pacientes',
  standalone: true,
  imports: [
    HeaderSophiaComponent,
    MatIcon,
    TablaPacientesComponent,
    PagerComponent,
    SearchBarComponent,
  ],
  templateUrl: './mis-pacientes.component.html',
  styleUrl: './mis-pacientes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MisPacientesComponent implements OnInit {

  private readonly pacienteService: PacienteService = inject(PacienteService);
  private readonly storage = inject(StorageService);
  private readonly router: Router = inject(Router);

  public listToEmit: WritableSignal<null | PatientFilterItem[]> = signal(null);
  public patients: WritableSignal<PatientListItem[]> = signal<PatientListItem[]>([] as PatientListItem[]);
  public total: WritableSignal<number> = signal(0);

  public currentPage: WritableSignal<number> = signal(1);
  public pageSize: WritableSignal<number> = signal(12);
  public isLoading: WritableSignal<boolean> = signal(false);
  public searchText: WritableSignal<string | null> = signal(null);

  private idMedico: WritableSignal<string | null> = signal(null);
  private idCentroMedico: WritableSignal<string | null> = signal(null);
  private loadList: EffectRef = effect(() => this.loadPatientList(), { allowSignalWrites: true });

  ngOnInit(): void {
    const doctorId = this.storage.getItem<Medicos>(StorageKeys.CURRENT_DOCTOR, StorageType.LOCAL)?.idMedico as string;
    this.idMedico.set(doctorId);

    const medicalCenterId = this.storage.getItem<CentroMedico>(StorageKeys.CURRENT_MEDICAL_CENTER, StorageType.LOCAL)?.idCentroMedico as string;
    this.idCentroMedico.set(medicalCenterId);
  }

  public async loadPatientList(): Promise<any> {
    if (typeof this.idMedico() !== 'string' || typeof this.idCentroMedico() !== 'string') return;
    this.isLoading.set(true);
    const options: IListRequestOptions = {
      page: this.currentPage(),
      size: this.pageSize(),
      ...(this.searchText() ? { search: this.searchText() } : {})
    }
    this.getList(options).subscribe((items: PatientListItem[]) => this.patients.set(items));
  }

  onUpdatePage(page: number) {
    this.currentPage.set(page);
  }

  patientClicked(event: any) {
    const { id, idPaciente } = event;
    this.router.navigate([`/paciente/${id ?? idPaciente}`]);
  }

  async updatedSearch(event: string) {
    const searchText = event.trim();

    if (event === '' && event !== this.searchText()) {
      this.currentPage.set(1);
      this.pageSize.set(12);
      return;
    }

    const options: IListRequestOptions = {
      page: 1,
      size: 12,
      search: searchText
    };
    const listToProcess: PatientListItem[] = await firstValueFrom(this.getList(options));
    const listToEmit: PatientFilterItem[] = listToProcess.map((item: PatientListItem) => ({
      idPaciente: item.paciente.idPaciente,
      nombreCompleto: `${item.paciente.nombres} ${item.paciente.apellidos}`,
      numeroDocumento: item.paciente.numeroDocumento,
      tipoDocumento: item.paciente.tipoDocumento.nombre
    }));

    this.listToEmit.set(listToEmit);
  }

  getList(options: IListRequestOptions): Observable<PatientListItem[]> {
    return this.pacienteService.listarPacientesDeMedico(this.idMedico()!, this.idCentroMedico()!, options).pipe(
      map((response: PatientsResponse) => {

        if (response.error) return [];
        const { total, items } = response.data;
        if (!items) return [];

        if (total !== this.total()) this.total.set(total);
        this.isLoading.set(false);
        return response.data.items;

      }),
    )
  }
}
